import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import unifiedApi from '../../utils/unifiedApi';

const parseErrorMessage = (errorMsg) => {
  if (!errorMsg) return null;
  // unifiedApi errors: "POST /api/... failed with status 409: {"message":"..."}"
  const jsonStart = errorMsg.indexOf('{');
  if (jsonStart !== -1) {
    try {
      const parsed = JSON.parse(errorMsg.substring(jsonStart));
      if (parsed.message) return parsed.message;
    } catch {
      // not valid JSON, fall through
    }
  }
  return errorMsg;
};

const AddMemberDialog = ({ open, onClose, onMemberAdded }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [sendInvitation, setSendInvitation] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    if (saving) return;
    setFirstName('');
    setLastName('');
    setEmail('');
    setSendInvitation(true);
    setError('');
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const result = await unifiedApi.addOrganizationMember({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        sendInvitation,
      });

      if (result) {
        onMemberAdded(result);
        handleClose();
      }
    } catch (err) {
      const message = parseErrorMessage(err?.message) || 'Failed to add member';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const isValid = firstName.trim() && lastName.trim() && email.trim();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Add Member</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              autoFocus
              fullWidth
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              fullWidth
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              inputProps={{ maxLength: 256 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendInvitation}
                  onChange={(e) => setSendInvitation(e.target.checked)}
                />
              }
              label="Send invitation email"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isValid || saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? 'Adding...' : 'Add Member'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddMemberDialog;
